import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { useSwipeToReveal } from "@/hooks/useSwipeToReveal";
import { cn } from "@/utils/cn";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";

interface SessionItemProps {
  sessionId: string;
  title: string;
  isActive: boolean;
  isStreaming: boolean;
  revealedSessionId: string | null;
  onReveal: (sessionId: string | null) => void;
  onNavigate: (sessionId: string) => void;
  onDelete: (sessionId: string) => void;
  /** Leave out for items that cannot be renamed; the menu then has no Rename. */
  onRename?: (sessionId: string, currentTitle: string) => void;
  /** Names one item in the button labels, as in "Delete conversation". */
  noun?: string;
  /**
   * An icon before the title. Its label is the tooltip and the accessible
   * description; the accessible name stays the title.
   */
  status?: { icon: ReactNode; label: string };
}

export const SessionItem = ({
  sessionId,
  title,
  isActive,
  isStreaming,
  revealedSessionId,
  onReveal,
  onNavigate,
  onDelete,
  onRename,
  noun = "conversation",
  status,
}: SessionItemProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const isOpen = revealedSessionId === sessionId;
  const nounLabel = `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`;

  const callbacks = useMemo(
    () => ({
      onOpen: () => onReveal(sessionId),
      onClose: () => onReveal(null),
    }),
    [onReveal, sessionId],
  );

  const { containerRef, contentStyle, offset, reset, handlers } = useSwipeToReveal(
    isOpen,
    callbacks,
  );

  // Auto-close when another item is revealed
  useEffect(() => {
    if (revealedSessionId !== null && revealedSessionId !== sessionId) {
      reset();
    }
  }, [revealedSessionId, sessionId, reset]);

  const swipeActionVisible = offset !== 0;

  return (
    <div ref={containerRef} className="relative overflow-hidden">
      {/* Delete action behind the row — hidden when closed to avoid subpixel bleed */}
      <button
        type="button"
        onClick={() => onDelete(sessionId)}
        aria-label={`Delete ${noun}`}
        aria-hidden={!swipeActionVisible}
        tabIndex={swipeActionVisible ? 0 : -1}
        className={cn(
          "absolute inset-y-0 right-0 flex w-16 items-center justify-center bg-red-500 text-white active:bg-red-600",
          !swipeActionVisible && "invisible pointer-events-none",
        )}
      >
        <Trash2 className="h-4 w-4" aria-hidden />
      </button>

      {/* Slideable row content */}
      <div
        style={contentStyle}
        className="group relative bg-white"
        {...handlers}
      >
        <button
          type="button"
          onClick={() => onNavigate(sessionId)}
          aria-current={isActive ? "page" : undefined}
          aria-label={
            isStreaming
              ? `${title || "Untitled"}, generating`
              : undefined
          }
          title={status?.label}
          className={cn(
            "flex w-full items-center px-5 py-1 text-left text-xs leading-6 transition-colors",
            isActive
              ? "text-zinc-900"
              : "text-zinc-400 hover:text-zinc-800",
          )}
        >
          {status && <span className="mr-2 flex shrink-0">{status.icon}</span>}
          <span
            className={cn(
              // Session titles are phrases, so they take the serif even
              // though the rest of the rail is set in the sans.
              "font-serif flex-1 overflow-hidden whitespace-nowrap pr-6",
              isStreaming && "animate-pulse-fast",
            )}
            style={{
              maskImage:
                "linear-gradient(to right, black 75%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to right, black 75%, transparent 100%)",
            }}
          >
            {title || "Untitled"}
          </span>
        </button>

        {/* Kebab menu (Rename / Delete) — appears on hover/focus or while open */}
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              aria-label={`${nounLabel} options`}
              className={cn(
                "absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-300 hover:text-zinc-700 group-hover:block group-focus-within:block focus:block",
                menuOpen ? "block" : "hidden",
              )}
            >
              <MoreVertical className="h-3.5 w-3.5" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {onRename && (
              <DropdownMenuItem
                className="gap-2.5"
                onSelect={() => onRename(sessionId, title)}
              >
                <Pencil className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
                Rename
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="gap-2.5 text-red-700 focus:text-red-700"
              onSelect={() => onDelete(sessionId)}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
